import { ButtonContent } from '../ui/ButtonContent';
/**
 * Billing settings page component.
 *
 * Shows either:
 * - Native pricing cards (when no active subscription) with a monthly/yearly
 *   toggle. Plan data is fetched from the metering service so prices and
 *   seat/credit quotas stay in sync with Stripe without code changes.
 *   Subscribe launches a Stripe Checkout Session tied to the org's customer
 *   (email locked).
 * - Current plan details with a "Manage Subscription" button (when subscribed).
 *
 * @module components/admin/BillingSettings
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import {
  useOrg,
  TrialAlreadyExtendedError,
  TrialNotActiveError,
} from '../../lib/OrgProvider';
import { useTrialStatus } from '../../lib/useTrialStatus';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { usePlanBadge } from '../../lib/usePlanBadge';
import { useOrgCredits } from '../../lib/useOrgCredits';
import { getPlanDisplayName, type SubscriptionDetails } from '../../lib/billing';
import {
  resolvePlanDisplay,
  formatAmount,
  monthlyEquivalent,
  yearlySavingsPercent,
  type ApiPricingPlan,
} from '../../lib/pricingPlans';

/** Props for the BillingSettings component. */
interface BillingSettingsProps {
  /** Organization ID for billing queries */
  orgId: string;
}

/** Response shape from GET /api/billing/pricing-plans. */
interface PricingPlansResponse {
  plans: ApiPricingPlan[];
}

/** Selected billing interval for the pricing toggle. */
type BillingInterval = 'month' | 'year';

/**
 * Subscription statuses the customer still pays for. A paying customer
 * changes plans in the billing portal: a second Checkout would create a
 * second live subscription and bill them twice. Matches the metering
 * service's checkout refusal.
 */
const PAYING_STATUSES = new Set(['active', 'past_due', 'unpaid']);

/** Metering error code for a checkout refused because the org already pays for a subscription. */
const ALREADY_SUBSCRIBED = 'already_subscribed';

/** Reads `{ detail: { error } }`, the metering service's error shape, or undefined. */
const meteringErrorCode = (payload: unknown): string | undefined => {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const detail: unknown = (payload as { detail?: unknown }).detail;
  if (typeof detail !== 'object' || detail === null) return undefined;
  const code: unknown = (detail as { error?: unknown }).error;
  return typeof code === 'string' ? code : undefined;
};

/**
 * Error code of a failed checkout response. The Pages proxy forwards the
 * metering service's body as a string in `upstream_detail`; an unwrapped
 * metering body is read too. Undefined when the body carries no code.
 */
const checkoutErrorCode = async (res: Response): Promise<string | undefined> => {
  try {
    const body: unknown = await res.json();
    const upstream: unknown = (body as { upstream_detail?: unknown } | null)?.upstream_detail;
    return meteringErrorCode(typeof upstream === 'string' ? JSON.parse(upstream) : body);
  } catch {
    return undefined;
  }
};

/**
 * Billing settings page with Stripe integration.
 * Renders native pricing cards for unsubscribed orgs, or plan management for subscribed orgs.
 */
export const BillingSettings: React.FC<BillingSettingsProps> = ({ orgId }) => {
  const { supabase } = useSupabase();
  const { hasPermission, extendTrial } = useOrg();
  const trialStatus = useTrialStatus();
  const [extendingTrial, setExtendingTrial] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);

  const canViewBilling = hasPermission('organization:view_billing');
  const canManageBilling = hasPermission('organization:manage_billing');

  /**
   * Handle the "Extend by 7 days" click. Maps backend error classes to simple,
   * user-facing copy per holito UI guidance (no em dashes, terse messages).
   */
  const handleExtendTrial = useCallback(async () => {
    setExtendingTrial(true);
    setTrialError(null);
    try {
      await extendTrial();
    } catch (err) {
      if (err instanceof TrialAlreadyExtendedError) {
        setTrialError('Your trial has already been extended.');
      } else if (err instanceof TrialNotActiveError) {
        setTrialError('Your trial is no longer active.');
      } else {
        setTrialError('Could not extend your trial. Please try again.');
      }
    } finally {
      setExtendingTrial(false);
    }
  }, [extendTrial]);
  const { planName, loading: planLoading } = usePlanBadge();
  const { credits } = useOrgCredits();
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [availablePlans, setAvailablePlans] = useState<ApiPricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [managingPortal, setManagingPortal] = useState(false);
  const [subscribingPriceId, setSubscribingPriceId] = useState<string | null>(null);
  // Default to yearly: research (Chargebee 2022) shows yearly-default increases
  // annual mix 24-31% vs monthly-default with no conversion penalty.
  const [interval, setInterval] = useState<BillingInterval>('year');

  useEffect(() => {
    if (planLoading) return undefined;
    let cancelled = false;

    const loadBilling = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setError('Not authenticated');
          setLoading(false);
          return;
        }

        const plansPromise = fetch('/api/billing/pricing-plans').then(async (res) => {
          if (!res.ok) throw new Error(`Pricing plans fetch failed: ${res.status}`);
          const data: PricingPlansResponse = await res.json();
          for (const plan of data.plans) {
            resolvePlanDisplay(plan.product_id);
          }
          return data.plans;
        });

        // Free tier, skip subscription fetch
        if (planName === 'Free') {
          const plans = await plansPromise;
          if (!cancelled) {
            setAvailablePlans(plans);
            setSubscription(null);
          }
          return;
        }

        const subscriptionPromise = fetch(
          `/api/billing/subscription?org_id=${encodeURIComponent(orgId)}`,
          {
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'X-Organization-ID': orgId,
            },
          },
        ).then(async (res) => {
          if (!res.ok) throw new Error(`Subscription fetch failed: ${res.status}`);
          return res.json() as Promise<SubscriptionDetails>;
        });

        const [plans, sub] = await Promise.all([plansPromise, subscriptionPromise]);
        if (cancelled) return;
        setAvailablePlans(plans);
        setSubscription(sub);
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'BillingSettings.loadBilling',
          { orgId },
        );
        if (!cancelled) {
          setError('Failed to load billing information. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadBilling();
    return () => { cancelled = true; };
  }, [supabase, orgId, planName, planLoading, retryCount]);

  /** Opens the Stripe Billing Portal for plan management / payment updates. */
  /**
   * Open the Stripe Customer Portal. Accepts an optional ``flowType`` which
   * deep-links directly to a specific screen, currently only
   * ``'payment_method_update'`` is supported server-side. When omitted, the
   * portal opens on its default home page (used by the "Manage Subscription"
   * button on active subs).
   */
  const openPortal = useCallback(async (flowType?: 'payment_method_update') => {
    setManagingPortal(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch('/api/billing/portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'X-Organization-ID': orgId,
        },
        body: flowType ? JSON.stringify({ flow_type: flowType }) : undefined,
      });

      if (!res.ok) {
        throw new Error(`Portal session failed: ${res.status}`);
      }

      const data = await res.json() as { url: string };
      window.location.href = data.url;
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'BillingSettings.openPortal',
        { orgId, flowType: flowType ?? null },
      );
      setError('Failed to open billing portal. Please try again.');
    } finally {
      setManagingPortal(false);
    }
  }, [supabase, orgId]);

  /** Existing "Manage Subscription" handler (portal home page). */
  const handleManageSubscription = useCallback(() => openPortal(), [openPortal]);

  /** Trial-card "Add payment method" handler, deep-links to add-PM screen. */
  const handleAddPaymentMethod = useCallback(
    () => openPortal('payment_method_update'),
    [openPortal],
  );

  /**
   * Kicks off a Stripe Checkout Session for the chosen plan. When the metering
   * service refuses because the org already pays for a subscription, the
   * billing portal opens instead, where plan changes happen.
   */
  const handleSubscribe = useCallback(async (priceId: string) => {
    setSubscribingPriceId(priceId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch('/api/billing/checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'X-Organization-ID': orgId,
        },
        body: JSON.stringify({ price_id: priceId }),
      });

      if (!res.ok) {
        if (await checkoutErrorCode(res) === ALREADY_SUBSCRIBED) {
          await openPortal();
          setSubscribingPriceId(null);
          return;
        }
        throw new Error(`Checkout session failed: ${res.status}`);
      }

      const data = await res.json() as { url: string };
      window.location.href = data.url;
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'BillingSettings.handleSubscribe',
        { orgId, priceId },
      );
      setError('Failed to start checkout. Please try again.');
      setSubscribingPriceId(null);
    }
  }, [supabase, orgId, openPortal]);

  /**
   * Format a Unix timestamp (seconds) or date string to a human-readable
   * format. Rendered in UTC so a billing date (period end / scheduled cancel)
   * shows the same calendar day for every viewer and matches Stripe's record,
   * instead of drifting by a day across timezones near a midnight boundary.
   */
  const formatDate = (value: string | number): string => {
    try {
      const ts = typeof value === 'number' ? value : Number(value);
      const ms = !isNaN(ts) && ts < 1e12 ? ts * 1000 : ts;
      return new Date(ms).toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return String(value);
    }
  };

  /**
   * Plans to show in the grid for the current interval selection.
   * Free-tier plans (unit_amount === 0) always show regardless of interval,
   * since they don't have a yearly variant. For each product, we prefer the
   * plan matching the selected interval; otherwise fall back to the only
   * available plan for that product (keeps Free visible on the Yearly tab).
   */
  const visiblePlans = useMemo(() => {
    const byProduct = new Map<string, ApiPricingPlan[]>();
    for (const plan of availablePlans) {
      resolvePlanDisplay(plan.product_id);
      const list = byProduct.get(plan.product_id) ?? [];
      list.push(plan);
      byProduct.set(plan.product_id, list);
    }

    const selected: ApiPricingPlan[] = [];
    for (const [, plans] of byProduct) {
      const match = plans.find((p) => p.interval === interval);
      if (match) {
        selected.push(match);
        continue;
      }
      // Free tier has no yearly counterpart, show its single plan anyway.
      const freeFallback = plans.find((p) => p.unit_amount === 0);
      if (freeFallback) selected.push(freeFallback);
    }

    return selected.sort((a, b) => a.unit_amount - b.unit_amount);
  }, [availablePlans, interval]);

  /** Average savings percent across products (for the Yearly toggle label). */
  const avgSavingsPct = useMemo(() => {
    const percents: number[] = [];
    const productIds: string[] = Array.from(new Set(availablePlans.map((p) => p.product_id)));
    for (const productId of productIds) {
      resolvePlanDisplay(productId);
      const monthly = availablePlans.find(
        (p) => p.product_id === productId && p.interval === 'month' && p.unit_amount > 0,
      );
      const yearly = availablePlans.find(
        (p) => p.product_id === productId && p.interval === 'year' && p.unit_amount > 0,
      );
      if (monthly && yearly) {
        percents.push(yearlySavingsPercent(monthly.unit_amount, yearly.unit_amount));
      }
    }
    if (percents.length === 0) return 0;
    return Math.round(percents.reduce((a, b) => a + b, 0) / percents.length);
  }, [availablePlans]);

  /**
   * Formatted copy describing the post-trial charge ("£109/month"), derived
   * from the subscription's bound price. Null when the trial isn't bound to
   * a price we recognise (backend/frontend drift, free tier, or before the
   * pricing-plans response has landed). TrialCard falls back to the plain
   * end-date copy in that case.
   */
  const trialPrice = useMemo<{ amount: string; interval: 'month' | 'year' } | null>(() => {
    const priceId = subscription?.price_id;
    if (!priceId) return null;
    const match = availablePlans.find((p) => p.price_id === priceId);
    if (!match || match.unit_amount <= 0) return null;
    return {
      amount: formatAmount(match.unit_amount, match.currency),
      interval: match.interval,
    };
  }, [subscription?.price_id, availablePlans]);

  if (loading) {
    return (
      <div role="status" aria-label="Loading billing" className="p-6 min-h-[640px] grid grid-cols-1 md:grid-cols-3 gap-4">
        {[0, 1, 2].map((tile) => <div key={tile} aria-hidden="true" className="min-h-[540px] rounded-xl bg-surface animate-pulse motion-reduce:animate-none" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-wash-danger rounded-lg text-danger-soft">
          {error}
          <button type="button" className="block mt-4 cursor-pointer text-sm underline" onClick={() => setRetryCount((count) => count + 1)}>Try again</button>
        </div>
      </div>
    );
  }

  const creditBalance = credits && (
    <div className="p-3 bg-background border border-border rounded-lg text-sm text-muted-foreground">
      <span className="font-semibold text-foreground">{credits.available_credits}</span>{' '}
      credit{credits.available_credits === 1 ? '' : 's'} available for audio and video conversations.
    </div>
  );

  const isPastDue = subscription?.status === 'past_due';
  const hasActiveSubscription = subscription && (subscription.status === 'active' || isPastDue);
  const isPendingCancellation = hasActiveSubscription && (!!subscription.cancel_at || subscription.cancel_at_period_end === true);

  if (hasActiveSubscription) {
    const activePlanName = getPlanDisplayName(subscription.product_id);
    // Prefer the explicit scheduled cancel date, which can sit past the
    // current period end. Fall back to the period end for a pure period-end
    // cancel where cancel_at may be null.
    const effectiveCancelDate = subscription.cancel_at ?? subscription.current_period_end;
    const dateValue = isPendingCancellation ? effectiveCancelDate : subscription.current_period_end;

    return (
      <div className="p-6 space-y-6">
        <h2 className="text-xl font-bold text-foreground">Billing & Subscription</h2>

        {creditBalance}

        {isPastDue && (
          <div className="p-4 bg-wash-danger rounded-lg flex items-center justify-between">
            <div>
              <p className="text-danger-soft font-medium">Payment failed</p>
              <p className="text-foreground/80 text-sm mt-1">
                Your last payment didn't go through. Update your payment method to keep your subscription active.
              </p>
            </div>
            <button aria-busy={managingPortal}
              onClick={handleManageSubscription}
              disabled={managingPortal}
              className="ml-4 shrink-0 px-4 py-2 bg-danger text-danger-foreground text-sm font-medium rounded-lg hover:bg-danger/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ButtonContent loading={managingPortal}>Update Payment Method</ButtonContent>
            </button>
          </div>
        )}

        <div className="bg-background border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Current Plan</h3>
              <p className="text-muted-foreground text-sm mt-1">Your organization's active subscription</p>
            </div>
            {isPastDue ? (
              <span className="px-3 py-1 bg-danger/20 text-danger text-sm font-medium rounded-full">
                Past Due
              </span>
            ) : (
              <span className="px-3 py-1 bg-success/20 text-success text-sm font-medium rounded-full">
                Active
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <p className="text-sm text-muted-foreground">Plan</p>
              <p className="text-lg font-semibold text-foreground">{activePlanName}</p>
            </div>
            {dateValue && (
              <div>
                <p className="text-sm text-muted-foreground">
                  {isPendingCancellation ? 'Cancels on' : 'Next billing date'}
                </p>
                <p className="text-lg font-semibold text-foreground">
                  {formatDate(dateValue)}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="text-lg font-semibold text-foreground capitalize">{subscription.status}</p>
            </div>
          </div>

          {isPendingCancellation && effectiveCancelDate && (
            <div className="p-3 bg-wash-warning rounded-lg text-foreground text-sm mb-4">
              Your subscription will cancel on {formatDate(effectiveCancelDate)}. You can resubscribe at any time.
            </div>
          )}

          <button aria-busy={managingPortal}
            onClick={handleManageSubscription}
            disabled={managingPortal}
            className="px-6 py-2.5 button-accent font-medium rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ButtonContent loading={managingPortal}>Manage Subscription</ButtonContent>
          </button>
        </div>
      </div>
    );
  }

  // No subscription: native pricing cards
  // A trialing org falls through to this branch (subscription.status !== 'active')
  // so the pricing cards stay visible and the user can still upgrade to Pro or
  // Business mid-trial via the existing Stripe Checkout path. A customer who
  // still pays for a subscription (e.g. unpaid) upgrades in the billing portal.
  const paysForSubscription = !!subscription && PAYING_STATUSES.has(subscription.status);
  const handleUpgrade = paysForSubscription ? handleManageSubscription : handleSubscribe;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-foreground">Choose a Plan</h2>
          <p className="text-muted-foreground mt-1">Text conversations stay free.</p>
        </div>
        <IntervalToggle value={interval} onChange={setInterval} savingsPct={avgSavingsPct} />
      </div>

      {creditBalance}

      {canViewBilling && trialStatus.isTrialing && (
        <TrialCard
          daysRemaining={trialStatus.daysRemaining}
          endsAt={trialStatus.endsAt}
          shouldShowExtendButton={trialStatus.shouldShowExtendButton}
          canManageBilling={canManageBilling}
          extending={extendingTrial}
          openingPortal={managingPortal}
          errorMessage={trialError}
          onExtend={handleExtendTrial}
          onAddPaymentMethod={handleAddPaymentMethod}
          trialPrice={trialPrice}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {visiblePlans.map((plan) => {
          const monthlyCounterpart = availablePlans.find(
            (p) => p.product_id === plan.product_id && p.interval === 'month' && p.unit_amount > 0,
          );
          return (
            <PlanCard
              key={plan.price_id}
              plan={plan}
              monthlyCounterpart={monthlyCounterpart}
              currentPlanName={planName}
              subscribing={subscribingPriceId === plan.price_id}
              onSubscribe={handleUpgrade}
            />
          );
        })}
      </div>

      <p className="text-sm text-muted-foreground">One person’s audio or video conversation uses one credit, even if they pause and resume.</p>

      {!trialStatus.isTrialing && (
        <p className="text-sm text-muted-foreground">New accounts include a 14-day Pro trial.</p>
      )}

      {/* Enterprise CTA */}
      <div className="pt-2">
        <p className="text-sm text-muted-foreground">
          Need something custom?{' '}
          <a
            href="/admin/settings?section=support"
            className="text-volt-soft hover:text-volt-soft underline transition-colors cursor-pointer"
          >
            Get in touch
          </a>
          {' '}for enterprise plans.
        </p>
      </div>
    </div>
  );
};

/** Monthly/Yearly segmented toggle. */
interface IntervalToggleProps {
  value: BillingInterval;
  onChange: (next: BillingInterval) => void;
  savingsPct: number;
}

const IntervalToggle: React.FC<IntervalToggleProps> = ({ value, onChange, savingsPct }) => {
  const base = 'cursor-pointer px-4 py-2 text-sm font-medium rounded-full transition-colors motion-reduce:transition-none';
  return (
    <div role="group" aria-label="Billing interval" className="inline-flex items-center gap-1 bg-background border border-border rounded-full p-1">
      <button
        type="button"
        aria-pressed={value === 'month'}
        onClick={() => onChange('month')}
        className={`${base} ${value === 'month' ? 'bg-surface text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      >
        Monthly
      </button>
      <button
        type="button"
        aria-pressed={value === 'year'}
        onClick={() => onChange('year')}
        className={`${base} ${value === 'year' ? 'bg-surface text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      >
        Yearly
        {savingsPct > 0 && (
          <span className="ml-2 px-1.5 py-0.5 text-sm font-semibold rounded-lg bg-volt/20 text-volt-soft uppercase tracking-wider">
            Save {savingsPct}%
          </span>
        )}
      </button>
    </div>
  );
};

/** Props for a single pricing-card tile. */
interface PlanCardProps {
  plan: ApiPricingPlan;
  /** Monthly-interval counterpart for the same product, used to strike-through the monthly price when yearly is selected. */
  monthlyCounterpart?: ApiPricingPlan;
  currentPlanName: string | null;
  subscribing: boolean;
  onSubscribe: (priceId: string) => void;
}

/** Renders one plan card: name, tagline, price, CTA, then feature list. */
const PlanCard: React.FC<PlanCardProps> = ({ plan, monthlyCounterpart, currentPlanName, subscribing, onSubscribe }) => {
  const display = resolvePlanDisplay(plan.product_id);

  const isCurrent = currentPlanName === plan.name || (plan.unit_amount === 0 && (!currentPlanName || currentPlanName === 'Free'));
  const isFree = plan.unit_amount === 0;

  // Reserve the same geometry for both billing intervals.
  const cardClass = display.highlighted
    ? 'relative bg-wash-accent rounded-xl p-6 flex flex-col min-h-[620px]'
    : 'relative bg-surface rounded-xl p-6 flex flex-col min-h-[620px]';

  // Yearly plans: show monthly-equivalent headline price; annual total goes in
  // the billing note so comparison with monthly stays clean.
  const showingYearly = plan.interval === 'year' && !isFree;
  const headlineAmount = showingYearly ? monthlyEquivalent(plan.unit_amount) : plan.unit_amount;
  const headlinePrice = formatAmount(headlineAmount, plan.currency);
  const currencyUpper = plan.currency.toUpperCase();
  const billingNote = isFree
    ? 'Free, always'
    : showingYearly
      ? `billed annually as ${formatAmount(plan.unit_amount, plan.currency)}`
      : 'billed monthly';

  const featuresHeader = display.inheritsFrom
    ? `Everything in ${display.inheritsFrom}, plus:`
    : 'Includes:';

  // Three visually distinct states:
  //  - current/disabled: solid grey, muted text (reads as inert)
  //  - primary (highlighted tier): solid primary-base
  //  - secondary (non-highlighted paid tier): outlined using the app's standard
  //    secondary button pattern (matches LoginPage.tsx). The border makes it
  //    clearly separable from the disabled state.
  const buttonClass = isCurrent
    ? 'w-full px-4 py-2.5 bg-surface text-muted-foreground font-medium rounded-lg cursor-not-allowed'
    : display.highlighted
      ? 'w-full px-4 py-2.5 button-accent font-medium rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
      : 'w-full px-4 py-2.5 bg-surface-hover text-foreground cursor-pointer font-medium rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className={cardClass}>


      {/* Name + tagline: title is always the visual anchor; price stays smaller */}
      <div>
        <h3 className="text-[22px] font-semibold text-foreground">{plan.name}</h3>
        <p className="text-sm text-muted-foreground mt-1">{display.tagline}</p>
      </div>

      {/* Price row: headline price + billing note. min-h keeps Free and paid
          cards visually aligned (paid note renders on 2 lines, Free on 1).
          key={plan.interval} remounts the block on toggle so the fade-in
          animation replays smoothly between monthly/yearly values. 200ms feels
          snappy on a toggle interaction (the shared .animate-fade-in runs 500ms
          which feels sluggish here). */}
      <div
        key={plan.interval}
        className="mt-6 flex items-baseline gap-3 min-h-[36px] animate-[fade-in_200ms_ease-out] motion-reduce:animate-none"
      >
        <span className="text-xl font-semibold text-foreground leading-none">{headlinePrice}</span>
        {showingYearly && monthlyCounterpart && (
          <span className="text-sm text-muted-foreground line-through leading-none">
            {formatAmount(monthlyCounterpart.unit_amount, monthlyCounterpart.currency)}
          </span>
        )}
        {!isFree && (
          <div className="flex flex-col text-sm text-muted-foreground leading-snug">
            <span>{currencyUpper} / month</span>
            <span>{billingNote}</span>
          </div>
        )}
        {isFree && (
          <span className="text-sm text-muted-foreground">{billingNote}</span>
        )}
      </div>

      {/* CTA: subtext line is rendered either way so the button sits at the
          same vertical position across cards. Non-actionable rows use an
          invisible placeholder to preserve the height. */}
      <div className="mt-5">
        <button aria-busy={subscribing}
          type="button"
          onClick={() => !isCurrent && !isFree && onSubscribe(plan.price_id)}
          disabled={isCurrent || isFree || subscribing}
          className={buttonClass}
        >
          <ButtonContent loading={subscribing}>{isCurrent
            ? 'Current plan'
            : isFree
              ? 'Free plan'
              : `Upgrade to ${plan.name}`}</ButtonContent>
        </button>
        <p className="mt-2 text-sm text-muted-foreground">
          {!isCurrent && !isFree ? 'Cancel anytime' : '\u00A0'}
        </p>
      </div>

      {/* Divider */}
      <hr className="my-5 border-border" />

      {/* Features: below the CTA */}
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-foreground mb-3">{featuresHeader}</h4>
        <ul className="space-y-2">
          <FeatureBullet>
            {plan.seats} {plan.seats === 1 ? 'seat' : 'seats'}
          </FeatureBullet>
          {!isFree && plan.credits_per_month > 0 && (
            <FeatureBullet>
              {plan.credits_per_month} audio/video credits / month
            </FeatureBullet>
          )}
          {isFree && <li className="text-sm text-muted-foreground">Audio/video conversations: No</li>}
          {display.bullets.map((bullet) => (
            <FeatureBullet key={bullet}>{bullet}</FeatureBullet>
          ))}
        </ul>
      </div>
    </div>
  );
};

/** Single feature-line item with check icon. */
const FeatureBullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start gap-2 text-sm text-muted-foreground">
    <svg className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
    <span>{children}</span>
  </li>
);

/** Props for the trial status card rendered above the pricing grid during a trial. */
interface TrialCardProps {
  /** Days remaining on the trial, ceiling-rounded. */
  daysRemaining: number;
  /** Trial end Date used in the "after {date}" phrase, or null to fall back to generic copy. */
  endsAt: Date | null;
  /** Whether the Extend CTA should be shown (<= 3 days left AND canExtend). */
  shouldShowExtendButton: boolean;
  /** Whether the viewer holds `organization:manage_billing` (controls both the
   *  Add payment method and Extend buttons). */
  canManageBilling: boolean;
  /** True while the extend request is in flight. */
  extending: boolean;
  /** True while the portal-session request is in flight. */
  openingPortal: boolean;
  /** User-visible error message from the most recent extend attempt, or null. */
  errorMessage: string | null;
  /** Handler bound to the Extend button. */
  onExtend: () => void;
  /** Handler bound to the Add payment method button (opens Stripe portal). */
  onAddPaymentMethod: () => void;
  /** Formatted charge amount + interval that kicks in when the trial ends,
   *  or null when the trial isn't bound to a known paid price. Used to make
   *  the "you'll be charged X" line explicit instead of generic. */
  trialPrice: { amount: string; interval: 'month' | 'year' } | null;
}

/**
 * Renders the trial status card for an active trial. Keeps copy simple and
 * free of em dashes per holito UI guidance. The Upgrade CTA on each pricing
 * card below this one still provides the path to Business or yearly Pro; the
 * card itself doesn't duplicate the Upgrade action.
 */
const TrialCard: React.FC<TrialCardProps> = ({
  daysRemaining,
  endsAt,
  shouldShowExtendButton,
  canManageBilling,
  extending,
  openingPortal,
  errorMessage,
  onExtend,
  onAddPaymentMethod,
  trialPrice,
}) => {
  const formatDate = (d: Date | null): string => {
    if (!d) return 'your trial end date';
    try {
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'your trial end date';
    }
  };

  /**
   * Build the card's body copy. When we know the bound price we name the
   * exact post-trial charge so the user isn't surprised ("£109/month
   * starting 6 May 2026"); otherwise we fall back to the plain end-date
   * line.
   */
  const bodyCopy = trialPrice
    ? `Add a payment method to keep Pro. You'll be charged ${trialPrice.amount}/${trialPrice.interval} starting ${formatDate(endsAt)}.`
    : `Add a payment method to keep Pro after ${formatDate(endsAt)}.`;

  return (
    <div
      data-testid="trial-card"
      className="rounded-xl bg-wash-warning p-6"
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="px-2 py-0.5 text-sm font-bold uppercase tracking-wider rounded-lg border bg-warning/20 text-warning-soft border-warning/30">
          Pro Trial
        </span>
        <span className="text-sm text-muted-foreground">
          {daysRemaining} days remaining
        </span>
      </div>

      <p className="text-sm text-muted-foreground">{bodyCopy}</p>

      {canManageBilling && (
        <div className="mt-4 flex flex-wrap gap-3">
          <button aria-busy={openingPortal}
            type="button"
            onClick={onAddPaymentMethod}
            disabled={openingPortal}
            className="rounded-lg px-4 py-2.5 bg-warning text-warning-foreground font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ButtonContent loading={openingPortal}>Add payment method</ButtonContent>
          </button>
          {shouldShowExtendButton && (
            <button aria-busy={extending}
              type="button"
              onClick={onExtend}
              disabled={extending}
              className="rounded-lg px-4 py-2.5 border border-warning/60 bg-transparent text-warning-soft font-medium hover:bg-warning/10 hover:border-warning hover:text-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ButtonContent loading={extending}>Extend by 7 days</ButtonContent>
            </button>
          )}
        </div>
      )}

      {errorMessage && (
        <p className="mt-3 text-sm text-danger">{errorMessage}</p>
      )}
    </div>
  );
};
